import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowUpRight, CalendarDays, Newspaper } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "";
  }
};

export default function Insights() {
  const [articles, setArticles] = useState([]);
  const [open, setOpen] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/insights`)
      .then((res) => setArticles(res.data.articles || []))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section id="insights" data-testid="insights-section" className="py-28 px-6 bg-[#faf9f6]">
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <Eyebrow>Regulatory Insights</Eyebrow>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
            <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl text-[#1b2a4e] tracking-tight max-w-xl leading-[1.1]">
              Perspectives on the <span className="italic text-[#a8824f]">regulatory landscape.</span>
            </h2>
            <p className="text-[#45506b] max-w-sm text-sm sm:text-base leading-relaxed">
              Short, practical briefings from the ComplyWise desk on what regulation means for your firm.
            </p>
          </div>
        </Reveal>

        {loading ? (
          <p data-testid="insights-loading" className="text-[#45506b] text-sm font-mono-alt tracking-[0.2em] uppercase">
            Loading insights...
          </p>
        ) : articles.length === 0 ? (
          <p data-testid="insights-empty" className="text-[#45506b] text-sm">
            Insights will appear here shortly.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {articles.map((a, i) => (
              <Reveal key={a.id} delay={(i % 4) * 0.08}>
                <motion.button
                  data-testid={`insight-card-${a.slug}`}
                  onClick={() => setOpen(a)}
                  whileHover={{ y: -6 }}
                  transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                  className="group w-full h-full text-left p-6 rounded-2xl bg-white border border-[#e6e1d5] hover:border-[#a8824f]/60 hover:shadow-[0_20px_50px_rgba(27,42,78,0.1)] transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#a8824f] flex flex-col"
                >
                  <div className="flex items-center justify-between mb-5">
                    <span className="p-2 rounded-lg bg-[#faf9f6] border border-[#e6e1d5] text-[#1b2a4e]">
                      <Newspaper size={16} />
                    </span>
                    <ArrowUpRight
                      size={15}
                      className="text-[#c8cdd8] group-hover:text-[#a8824f] transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    />
                  </div>
                  <span className="font-mono-alt text-[10px] uppercase tracking-[0.25em] text-[#a8824f] mb-2 block">
                    {a.category}
                  </span>
                  <h3 className="font-display text-xl text-[#1b2a4e] leading-snug mb-3">{a.title}</h3>
                  <p className="text-sm text-[#45506b] leading-relaxed mb-5 flex-1">{a.excerpt}</p>
                  <span className="flex items-center gap-2 font-mono-alt text-[10px] uppercase tracking-[0.2em] text-[#8a93a8]">
                    <CalendarDays size={12} /> {fmtDate(a.published_at)}
                  </span>
                </motion.button>
              </Reveal>
            ))}
          </div>
        )}

        <AnimatePresence>
          {open && (
            <motion.div
              data-testid="insight-reader"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="mt-12 rounded-3xl bg-white border border-[#e6e1d5] p-8 sm:p-12 shadow-[0_24px_70px_rgba(27,42,78,0.1)]"
            >
              <button
                data-testid="insight-back-button"
                onClick={() => setOpen(null)}
                className="group flex items-center gap-2 text-sm font-semibold text-[#1b2a4e] hover:text-[#a8824f] transition-colors mb-8 focus:outline-none focus:ring-2 focus:ring-[#a8824f] rounded"
              >
                <ArrowLeft size={16} className="transition-transform duration-300 group-hover:-translate-x-1" />
                All insights
              </button>
              <span className="font-mono-alt text-[10px] uppercase tracking-[0.3em] text-[#a8824f] block mb-3">
                {open.category} · {fmtDate(open.published_at)}
              </span>
              <h3 className="font-display font-semibold text-2xl sm:text-3xl lg:text-4xl text-[#1b2a4e] tracking-tight mb-6 max-w-3xl">
                {open.title}
              </h3>
              <div className="space-y-5 max-w-3xl">
                {open.body.split("\n\n").map((para, idx) => (
                  <p key={idx} className="text-[#45506b] text-base leading-relaxed">
                    {para}
                  </p>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
